// Módulo Arquivos — botão "Arquivar" usado dentro dos relatórios (01/10/2026, Walber).
// Mostra o nome automático, onde vai ficar, se já existe versão anterior e os avisos;
// arquivar NUNCA é bloqueado (decisão do Walber): com aviso, o botão vira "Arquivar mesmo assim"
// e os avisos ficam gravados junto com o arquivo. O botão não sai na impressão (.noprint).
import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { useAuth } from './AuthContext';
import { Arquivo, arquivosApi, formatarDataHora, infoPasta, mensagemDeErro } from './arquivosApi';
import { OpcoesCopia, PedidoArquivo, abrirNoArquivos, arquivarElemento, versaoAtual } from './arquivarCopia';

const ACENTO = '#0E5A4A';
const MONO = "'JetBrains Mono', ui-monospace, monospace";

interface Props {
  /** Devolve a área impressa do relatório (ex.: document.getElementById('fad07-papel')). */
  alvo?: () => Element | null;
  /** Alternativa à cópia da tela: gera o arquivo pronto (ex.: o PDF da Programação do LIRAa). */
  gerar?: () => Promise<{ arquivo: Blob; nome: string }>;
  /** Dados do documento; null enquanto o relatório não foi gerado. */
  pedido: PedidoArquivo | null;
  rotulo?: string;
  disabled?: boolean;
  /** Ajustes só da cópia (título no alto, folha deitada). */
  opcoes?: OpcoesCopia;
}

type Resultado = { tom: 'success' | 'warning' | 'error'; texto: string; pasta?: string };

export default function BotaoArquivar({ alvo, gerar, pedido, rotulo = 'Arquivar', disabled, opcoes }: Props) {
  const { token } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [anterior, setAnterior] = useState<Arquivo | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState<Resultado | null>(null);

  const avisos = pedido?.avisos ?? [];

  const abrir = async () => {
    if (!pedido) return;
    setErro('');
    setAnterior(null);
    setAberto(true);
    setConferindo(true);
    try { setAnterior(await versaoAtual(token, pedido)); } catch { setAnterior(null); } finally { setConferindo(false); }
  };

  const confirmar = async () => {
    if (!pedido) return;
    const el = gerar ? null : (alvo ? alvo() : null);
    if (!gerar && !el) { setErro('O relatório ainda não está na tela. Gere o relatório e tente de novo.'); return; }
    setSalvando(true);
    setErro('');
    try {
      let novo: Arquivo;
      if (gerar) {
        const g = await gerar();
        novo = await arquivosApi.arquivar(token, { ...pedido, arquivo: g.arquivo, nomeArquivo: g.nome });
      } else {
        novo = await arquivarElemento(token, el as Element, pedido, opcoes);
      }
      setAberto(false);
      setResultado({
        tom: avisos.length ? 'warning' : 'success',
        texto: avisos.length
          ? `Arquivado com ${avisos.length === 1 ? '1 aviso' : `${avisos.length} avisos`} · ${novo.titulo} (v${novo.versao})`
          : `Arquivado com sucesso · ${novo.titulo} (v${novo.versao})`,
        pasta: novo.pasta,
      });
    } catch (e) {
      setErro(`${mensagemDeErro(e)} Nada foi perdido: o relatório continua na tela.`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <Button className="noprint no-print" variant="outlined" startIcon={<ArchiveOutlinedIcon />} onClick={abrir}
        disabled={disabled || !pedido}
        sx={{ minHeight: 40, textTransform: 'none', fontWeight: 700, borderRadius: 2, color: ACENTO, borderColor: ACENTO, bgcolor: '#fff',
          '&:hover': { borderColor: ACENTO, bgcolor: '#E3EFEA' } }}>
        {rotulo}
      </Button>

      <Dialog className="noprint no-print" open={aberto} onClose={() => !salvando && setAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Arquivar este documento?</DialogTitle>
        {pedido && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: '#5A6661', fontSize: 14 }}>Uma cópia congelada, igual à impressão, vai para o Arquivos.</Typography>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5A6661', mb: 0.75 }}>Nome do arquivo (gerado sozinho)</Typography>
              <Box sx={{ px: 1.75, py: 1.5, bgcolor: '#F4F6F4', border: '1px solid #E1E6E2', borderRadius: 2, fontFamily: MONO, fontSize: 14, overflowWrap: 'anywhere' }}>
                {pedido.titulo}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#5A6661', mb: 0.5 }}>Onde vai ficar</Typography>
              <Typography sx={{ fontWeight: 600 }}>Pasta “{infoPasta(pedido.pasta).nome}”</Typography>
            </Box>
            {conferindo ? (
              <Typography sx={{ fontSize: 13, color: '#5A6661' }}>Conferindo se já existe cópia anterior…</Typography>
            ) : anterior ? (
              <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 14 } }}>
                <b>Este documento já foi arquivado antes.</b> A v{anterior.versao} é de {formatarDataHora(anterior.criado_em)}.
                Esta cópia será a <b>v{anterior.versao + 1}</b>. A v{anterior.versao} continua guardada, marcada como substituída.
              </Alert>
            ) : (
              <Typography sx={{ fontSize: 13, color: '#5A6661' }}>Primeira cópia deste documento.</Typography>
            )}
            {avisos.length > 0 && (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: 14, width: '100%' } }}>
                <b>{avisos.length === 1 ? '1 aviso' : `${avisos.length} avisos`} · dá para arquivar mesmo assim</b>
                {avisos.map(a => <Box key={a} component="span" sx={{ display: 'block', mt: 0.5 }}>• {a}</Box>)}
                <Box component="span" sx={{ display: 'block', mt: 0.75, fontSize: 13 }}>Os avisos ficam gravados junto com o arquivo.</Box>
              </Alert>
            )}
            {erro && <Alert severity="error">{erro}</Alert>}
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAberto(false)} disabled={salvando} variant="outlined"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, color: '#17211D', borderColor: '#CBD2CC' }}>Cancelar</Button>
          <Button onClick={confirmar} disabled={salvando || conferindo} variant="contained"
            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, bgcolor: ACENTO, boxShadow: 'none', '&:hover': { bgcolor: '#0A4236', boxShadow: 'none' } }}>
            {salvando ? 'Arquivando…' : erro ? 'Tentar de novo' : avisos.length ? 'Arquivar mesmo assim' : 'Arquivar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar className="noprint no-print" open={!!resultado} autoHideDuration={8000} onClose={() => setResultado(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {resultado ? (
          <Alert severity={resultado.tom} variant="filled" onClose={() => setResultado(null)}
            action={resultado.pasta ? (
              <Button color="inherit" size="small" onClick={() => abrirNoArquivos(resultado.pasta as string)} sx={{ fontWeight: 700 }}>Abrir</Button>
            ) : undefined}>
            {resultado.texto}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
